import { EnvelopeIcon, PhoneIcon, StarIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { generatePath, NavLink } from "react-router-dom";

import Headline from "~/components/Headline/Headline";
import { CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH } from "~/constants/appRoutes.ts";
import type {
	CentralInvoiceRecipient,
} from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientTypes.ts";
import { nl2br } from "~/utils/stringUtils.tsx";

interface Props {
	clientDisplayName?: string,
	invoiceRecipient: CentralInvoiceRecipient,
}

const CentralInvoiceRecipientContactCard: React.FC<Props> = ({
	clientDisplayName,
	invoiceRecipient,
}) => {
	const {
		clientId,
		displayName,
		legalName,
		supportContactEmail,
		supportContactPhone,
	} = invoiceRecipient;
	const isMissingEInvoiceEmail = !invoiceRecipient.invoiceEmail;
	return (
		<div
			className={clsx(
				"isolate col-span-1 transform-gpu divide-y divide-gray-200 rounded-lg shadow transition-transform duration-200",
				"bg-white",
				"hover:shadow-md",
			)}
		>
			<NavLink to={generatePath(CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH, {
				clientId,
				centralInvoiceRecipientId: invoiceRecipient.id,
			})}
					 className="group flex w-full items-center justify-between space-x-6 rounded-lg bg-white p-6">
				<div className="flex-1">
					<div className="flex items-center justify-between space-x-3">
						<Headline type="h5"
								  className="text-inherit group-hover:text-secondary-500">
							{displayName}
						</Headline>
					</div>

					{!clientDisplayName && legalName &&
						<div className="mt-1 flex flex-col items-start truncate whitespace-pre-wrap text-sm">
							{nl2br(legalName)}
						</div>}
					{clientDisplayName && (
						<div className="mt-1 flex flex-row items-center gap-1 truncate text-sm text-gray-500">
							<StarIcon className="size-4" />
							{clientDisplayName}
						</div>
					)}
					{isMissingEInvoiceEmail && <div className="mt-4 rounded-md bg-danger-500 px-2 py-0.5 text-center text-sm text-white">E-Mail Adresse für Rechnungsempfang fehlt.</div> }
				</div>

			</NavLink>
			{(supportContactEmail || supportContactPhone) && (
				<div>
					<div className="-mt-px flex divide-x divide-gray-200">
						{supportContactEmail && (
							<div className="flex w-0 flex-1">
								<a
									href={`mailto:${supportContactEmail}`}
									className={clsx(
										"relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 py-4",
										"border border-transparent",
										"text-sm font-semibold text-gray-700",
										"hover:bg-secondary-500 hover:text-white",
										"transition-all duration-200",
										supportContactPhone ? "rounded-bl-lg" : "rounded-b-lg",
									)}
								>
									<EnvelopeIcon className="size-5 text-current"
												  aria-hidden="true" />
									E-Mail an Support
								</a>
							</div>
						)}
						{supportContactPhone && (
							<div className="-ml-px flex w-0 flex-1">
								<a
									href={`tel:${supportContactPhone}`}
									className={clsx(
										"relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 py-4",
										"border border-transparent",
										"text-sm font-semibold text-gray-900",
										"hover:bg-secondary-500 hover:text-white",
										"transition-all duration-200",
										supportContactEmail ? "rounded-br-lg" : "rounded-b-lg",
									)}
								>
									<PhoneIcon className="size-5 text-current"
											   aria-hidden="true" />
									Support anrufen
								</a>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default CentralInvoiceRecipientContactCard;
